import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Monitor,
  Sparkles,
  Play,
  Pause,
  Square,
  Camera,
  Clock,
  Eye,
  SkipForward,
  AlertCircle,
  Loader2,
  ChevronRight,
  Calendar,
  CheckCircle2,
  XCircle,
  Shield,
} from "lucide-react";
import { screenCaptureApi } from "../../services/api";
import PremiumModal from "../ui/PremiumModal";
import ScreenCaptureReview from "./ScreenCaptureReview";

// ─── Types ──────────────────────────────────────────────────────────────────

interface FrameAnalysis {
  id: string;
  timestamp: string;
  activityType: string;
  matterName?: string;
  appTitle?: string;
  skipped: boolean;
}

interface PastSession {
  _id: string;
  createdAt: string;
  duration: string;
  totalCaptures: number;
  entriesGenerated: number;
  status: string;
}

type CaptureState = "idle" | "recording" | "paused" | "review";

// ─── Helpers ────────────────────────────────────────────────────────────────

const activityIcons: Record<string, string> = {
  drafting: "Drafting",
  email: "Email",
  research: "Research",
  review: "Review",
  communication: "Communication",
  filing: "Filing",
  billing: "Billing",
  unknown: "Activity",
};

function formatElapsed(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

function formatSessionDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("en-CA", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

// ─── Component ──────────────────────────────────────────────────────────────

const ScreenCaptureWidget: React.FC<{ onDataChange?: () => void }> = ({
  onDataChange,
}) => {
  // State
  const [captureState, setCaptureState] = useState<CaptureState>("idle");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [frames, setFrames] = useState<FrameAnalysis[]>([]);
  const [totalCaptures, setTotalCaptures] = useState(0);
  const [skippedCount, setSkippedCount] = useState(0);
  const [mattersDetected, setMattersDetected] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [pastSessions, setPastSessions] = useState<PastSession[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [browserSupported, setBrowserSupported] = useState(true);

  // Refs
  const streamRef = useRef<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const elapsedRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const previousHashRef = useRef<string | undefined>(undefined);
  const pausedRef = useRef(false);

  // Check browser support
  useEffect(() => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
      setBrowserSupported(false);
    }
  }, []);

  // Load past sessions
  const loadPastSessions = useCallback(async () => {
    try {
      setLoadingSessions(true);
      const res = await screenCaptureApi.sessions({ limit: 3, sort: "-createdAt" });
      setPastSessions(res.data?.data || res.data || []);
    } catch (err) {
      console.error("Failed to load past sessions:", err);
    } finally {
      setLoadingSessions(false);
    }
  }, []);

  useEffect(() => {
    loadPastSessions();
  }, [loadPastSessions]);

  // Elapsed timer
  useEffect(() => {
    if (captureState === "recording") {
      elapsedRef.current = setInterval(() => {
        setElapsed((prev) => prev + 1);
      }, 1000);
      return () => {
        if (elapsedRef.current) clearInterval(elapsedRef.current);
      };
    } else if (captureState === "paused") {
      if (elapsedRef.current) clearInterval(elapsedRef.current);
    }
  }, [captureState]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (elapsedRef.current) clearInterval(elapsedRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, []);

  // ─── Capture logic ──────────────────────────────────────────────────────────

  const captureFrame = useCallback(async (sid: string) => {
    if (pausedRef.current) return;
    if (!videoRef.current || !ctxRef.current || !canvasRef.current) return;

    try {
      ctxRef.current.drawImage(videoRef.current, 0, 0, 1280, 720);
      const dataUrl = canvasRef.current.toDataURL("image/jpeg", 0.6);
      const imageBase64 = dataUrl.split(",")[1];

      const res = await screenCaptureApi.frame({
        sessionId: sid,
        imageBase64,
        previousHash: previousHashRef.current,
      });

      previousHashRef.current = res.data.hash;

      const analysis = res.data.analysis;
      const skipped = res.data.skipped === true;

      const newFrame: FrameAnalysis = {
        id: `frame-${Date.now()}`,
        timestamp: new Date().toLocaleTimeString("en-CA", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        }),
        activityType: analysis?.activityType || "unknown",
        matterName: analysis?.matterName,
        appTitle: analysis?.appTitle,
        skipped,
      };

      setFrames((prev) => [newFrame, ...prev].slice(0, 50));
      setTotalCaptures((prev) => prev + 1);

      if (skipped) {
        setSkippedCount((prev) => prev + 1);
      } else if (analysis?.matterName) {
        setMattersDetected((prev) => new Set(prev).add(analysis.matterName));
      }
    } catch (err) {
      console.error("Frame capture failed:", err);
    }
  }, []);

  const startCapture = async () => {
    setError(null);

    try {
      // 1. Create server session
      const { data } = await screenCaptureApi.start();
      const sid = data.sessionId;
      setSessionId(sid);

      // 2. Request screen capture permission
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });

      streamRef.current = stream;

      // 3. Set up off-screen video + canvas
      const video = document.createElement("video");
      video.srcObject = stream;
      video.muted = true;
      await video.play();
      videoRef.current = video;

      const canvas = document.createElement("canvas");
      canvas.width = 1280;
      canvas.height = 720;
      canvasRef.current = canvas;
      ctxRef.current = canvas.getContext("2d");

      // 4. Start recording state
      previousHashRef.current = undefined;
      pausedRef.current = false;
      setFrames([]);
      setTotalCaptures(0);
      setSkippedCount(0);
      setMattersDetected(new Set());
      setElapsed(0);
      setCaptureState("recording");

      // 5. Capture first frame immediately, then every 60s
      captureFrame(sid);
      intervalRef.current = setInterval(() => captureFrame(sid), 30000);

      // 6. Handle browser "Stop sharing"
      stream.getVideoTracks()[0].onended = () => {
        handleStopCapture(sid);
      };
    } catch (err: any) {
      if (err.name === "NotAllowedError") {
        setError("Screen capture permission denied. Click Start to try again.");
      } else {
        setError("Failed to start screen capture. Please try again.");
        console.error("Screen capture error:", err);
      }
    }
  };

  const handlePause = () => {
    pausedRef.current = true;
    setCaptureState("paused");
  };

  const handleResume = () => {
    pausedRef.current = false;
    setCaptureState("recording");
  };

  const handleStopCapture = async (sid?: string) => {
    const id = sid || sessionId;

    // Stop intervals
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (elapsedRef.current) {
      clearInterval(elapsedRef.current);
      elapsedRef.current = null;
    }

    // Stop stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }

    // Stop server session
    if (id) {
      try {
        await screenCaptureApi.stop(id);
      } catch (err) {
        console.error("Failed to stop session:", err);
      }
    }

    setCaptureState("idle");
    setShowReviewModal(true);
  };

  const handleReviewClose = () => {
    setShowReviewModal(false);
    setSessionId(null);
    loadPastSessions();
  };

  const handleReviewAccepted = () => {
    setShowReviewModal(false);
    setSessionId(null);
    loadPastSessions();
    onDataChange?.();
  };

  const handleReviewPastSession = (session: PastSession) => {
    setSessionId(session._id);
    setShowReviewModal(true);
  };

  // ─── Render: Not supported ──────────────────────────────────────────────────

  if (!browserSupported) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
            <AlertCircle className="w-5 h-5 text-red-500" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-800">
              Screen Capture Not Supported
            </p>
            <p className="text-xs text-slate-500">
              Your browser does not support the Screen Capture API. Please use
              Chrome, Edge, or Firefox.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ─── Render: Recording state ────────────────────────────────────────────────

  if (captureState === "recording" || captureState === "paused") {
    const uniqueMatters = mattersDetected.size;

    return (
      <>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          {/* Recording header */}
          <div className="px-5 py-4 bg-gradient-to-r from-slate-900 to-slate-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {/* Pulsing red dot */}
                <div className="relative">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  {captureState === "recording" && (
                    <div className="absolute inset-0 w-3 h-3 rounded-full bg-red-500 animate-ping" />
                  )}
                </div>
                <span className="text-white font-semibold text-sm">
                  {captureState === "recording" ? "Recording" : "Paused"}
                </span>
              </div>
              <span className="text-white font-mono text-xl font-bold tracking-wider">
                {formatElapsed(elapsed)}
              </span>
            </div>

            {/* Progress bar */}
            <div className="mt-3 h-1 bg-white/10 rounded-full overflow-hidden">
              {captureState === "recording" ? (
                <motion.div
                  className="h-full bg-emerald-400 rounded-full"
                  initial={{ width: "0%" }}
                  animate={{ width: "100%" }}
                  transition={{ duration: 60, ease: "linear", repeat: Infinity }}
                />
              ) : (
                <div className="h-full bg-amber-400 rounded-full w-1/2" />
              )}
            </div>
          </div>

          {/* Live activity feed */}
          <div className="p-5">
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Eye className="w-3.5 h-3.5" />
              Live Activity
            </h4>

            {frames.length === 0 ? (
              <div className="flex items-center gap-2 text-sm text-slate-400 py-4">
                <Loader2 className="w-4 h-4 animate-spin text-emerald-500" />
                Waiting for first capture...
              </div>
            ) : (
              <div className="space-y-1.5 max-h-52 overflow-y-auto">
                <AnimatePresence initial={false}>
                  {frames.slice(0, 8).map((frame) => (
                    <motion.div
                      key={frame.id}
                      initial={{ opacity: 0, height: 0, y: -10 }}
                      animate={{ opacity: 1, height: "auto", y: 0 }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.25 }}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg ${
                        frame.skipped
                          ? "bg-slate-50 text-slate-400"
                          : "bg-emerald-50/50"
                      }`}
                    >
                      <div
                        className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          frame.skipped ? "bg-slate-300" : "bg-emerald-500"
                        }`}
                      />
                      <span className="text-xs text-slate-400 font-mono w-16 flex-shrink-0">
                        {frame.timestamp}
                      </span>
                      {frame.skipped ? (
                        <span className="text-xs text-slate-400 italic">
                          Skipped (no change)
                        </span>
                      ) : (
                        <div className="flex-1 min-w-0">
                          <span className="text-xs font-medium text-slate-700">
                            {activityIcons[frame.activityType] || "Activity"}
                          </span>
                          {frame.matterName && (
                            <span className="text-xs text-slate-500">
                              {" \u2014 "}
                              {frame.matterName}
                            </span>
                          )}
                          {frame.appTitle && (
                            <p className="text-[10px] text-slate-400 truncate mt-0.5">
                              {frame.appTitle}
                            </p>
                          )}
                        </div>
                      )}
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}

            {/* Stats row */}
            <div className="flex items-center gap-4 mt-4 pt-3 border-t border-slate-100">
              <span className="text-xs text-slate-500">
                <span className="font-semibold text-slate-700">{totalCaptures}</span>{" "}
                Captures
              </span>
              <span className="text-xs text-slate-500">
                <span className="font-semibold text-slate-700">{skippedCount}</span>{" "}
                Skipped
              </span>
              <span className="text-xs text-slate-500">
                <span className="font-semibold text-slate-700">{uniqueMatters}</span>{" "}
                Matters
              </span>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-3 mt-4">
              {captureState === "recording" ? (
                <button
                  onClick={handlePause}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors"
                >
                  <Pause className="w-4 h-4" />
                  Pause
                </button>
              ) : (
                <button
                  onClick={handleResume}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-xl transition-colors"
                >
                  <Play className="w-4 h-4" />
                  Resume
                </button>
              )}
              <button
                onClick={() => handleStopCapture()}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold text-white bg-[#0F5132] hover:bg-[#146C43] rounded-xl transition-colors shadow-sm"
              >
                <Square className="w-4 h-4" />
                Stop & Review
              </button>
            </div>
          </div>
        </div>

        {/* Review Modal */}
        <PremiumModal
          open={showReviewModal}
          onClose={handleReviewClose}
          title="Review Captured Time"
          subtitle="AI-generated time entries from your screen activity"
          icon={<Sparkles className="w-5 h-5 text-emerald-600" />}
          size="xl"
        >
          {sessionId && (
            <ScreenCaptureReview
              sessionId={sessionId}
              onClose={handleReviewClose}
              onAccepted={handleReviewAccepted}
            />
          )}
        </PremiumModal>
      </>
    );
  }

  // ─── Render: Idle state ─────────────────────────────────────────────────────

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {/* Gradient header */}
        <div
          className="px-5 py-5"
          style={{
            background:
              "linear-gradient(135deg, #0F5132 0%, #146C43 40%, #198754 100%)",
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <Monitor className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                  AI Screen Capture
                  <span className="text-[10px] font-bold bg-amber-400 text-amber-900 px-2 py-0.5 rounded-full uppercase tracking-wider">
                    Elite
                  </span>
                </h3>
                <p className="text-emerald-100 text-xs mt-0.5">
                  Automatically create time entries from your screen
                </p>
              </div>
            </div>
            <Sparkles className="w-6 h-6 text-emerald-200/50" />
          </div>
        </div>

        <div className="p-5">
          {/* Feature bullets */}
          <div className="space-y-2.5 mb-5">
            {[
              { icon: Camera, text: "Captures every 60 seconds" },
              { icon: Eye, text: "AI identifies matters and activities" },
              { icon: Sparkles, text: "Professional billing narratives generated" },
            ].map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-2.5">
                <div className="w-6 h-6 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-3.5 h-3.5 text-emerald-600" />
                </div>
                <span className="text-sm text-slate-600">{text}</span>
              </div>
            ))}
          </div>

          {/* Privacy note */}
          <div className="flex items-start gap-2 mb-5 p-3 bg-slate-50 rounded-lg border border-slate-100">
            <Shield className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-slate-500 leading-relaxed">
              Screenshots are processed by AI and immediately deleted. They are
              never stored or visible to anyone else. Your privacy is fully
              protected.
            </p>
          </div>

          {/* Error message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-2 mb-4 p-3 bg-red-50 border border-red-100 rounded-lg"
            >
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
              <p className="text-xs text-red-600">{error}</p>
              <button
                onClick={() => setError(null)}
                className="ml-auto text-red-400 hover:text-red-600"
              >
                <XCircle className="w-4 h-4" />
              </button>
            </motion.div>
          )}

          {/* Start button */}
          <button
            onClick={startCapture}
            className="w-full flex items-center justify-center gap-2.5 px-5 py-3.5 text-sm font-bold text-white bg-[#0F5132] hover:bg-[#146C43] rounded-xl transition-all shadow-sm hover:shadow-md active:scale-[0.98]"
          >
            <Play className="w-4 h-4" />
            Start Auto-Capture
          </button>

          {/* Past sessions */}
          {pastSessions.length > 0 && (
            <div className="mt-5 pt-4 border-t border-slate-100">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5" />
                  Past Sessions ({pastSessions.length})
                </h4>
              </div>

              <div className="space-y-2">
                {pastSessions.map((session) => (
                  <button
                    key={session._id}
                    onClick={() => handleReviewPastSession(session)}
                    className="w-full flex items-center gap-3 p-3 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors text-left group"
                  >
                    <div className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center flex-shrink-0">
                      <Camera className="w-3.5 h-3.5 text-slate-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-700">
                        {formatSessionDate(session.createdAt)}
                        <span className="text-slate-400 ml-2">
                          {session.duration}
                        </span>
                      </p>
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        {session.entriesGenerated} entries generated
                        {" \u00B7 "}
                        {session.totalCaptures} captures
                      </p>
                    </div>
                    <span
                      className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                        session.status === "completed"
                          ? "bg-emerald-50 text-emerald-600"
                          : session.status === "partial"
                          ? "bg-amber-50 text-amber-600"
                          : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      {session.status === "completed"
                        ? "Accepted"
                        : session.status === "partial"
                        ? "Partial"
                        : "Pending"}
                    </span>
                    <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 transition-colors" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {loadingSessions && pastSessions.length === 0 && (
            <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-center py-4">
              <Loader2 className="w-4 h-4 animate-spin text-slate-300" />
            </div>
          )}
        </div>
      </div>

      {/* Review Modal (for past session re-review or post-stop) */}
      <PremiumModal
        open={showReviewModal}
        onClose={handleReviewClose}
        title="Review Captured Time"
        subtitle="AI-generated time entries from your screen activity"
        icon={<Sparkles className="w-5 h-5 text-emerald-600" />}
        size="xl"
      >
        {sessionId && (
          <ScreenCaptureReview
            sessionId={sessionId}
            onClose={handleReviewClose}
            onAccepted={handleReviewAccepted}
          />
        )}
      </PremiumModal>
    </>
  );
};

export default ScreenCaptureWidget;
